import moment from 'moment';
import { t } from '../../utils/i18n';
import { handleEventRoomNullUI, handleEventRoomUI, runRescheduleFlow } from "../../modules/actions/appointmentConfirmedActions";
import { getProviderFullName } from "../../modules/actions/providerActions";
import { params, updateUrlWithEventId, openBookAppointment } from "../../modules/events/movePages";
import { navigate } from "../../modules/events/movePages";
import { SCREEN } from "../../utils/constants";
import { EVENT_ID_PARAM, DROPOFF_RESUME_PARAM } from "../../utils/constants";
import { procedureDisplayName } from '../../utils/procedureDisplay';
import { isPhoneBooking, isVirtualVisit, remoteVisitLabelKey } from '../../utils/deliveryMode';
import { loadAppointmentCancel } from "./appointmentCancel";
import { appointmentCancelListeners } from "../../modules/events/appointmentCancelListeners";
import { appointmentSummary, eventPayload, eventRoom, form, locations, primaryColor, procedures as allProcedures, selectedAddons, setEventPayload } from "../../utils/variables";
import { getEvevntInfoById, getEventResume } from "../../services/selectTimeService";
import {
    SELECTORS,
    MESSAGES,
    APPOINTMENT_STATUS,
    PARTICIPANT_TYPES,
    COLORS
} from "../../utils/appointmentConfirmedConstants";

export async function loadAppointmentConfirmed() {
    if (!eventPayload) return;

    if (eventPayload.id && !params(EVENT_ID_PARAM)) {
        updateUrlWithEventId(
            eventPayload.id,
            eventPayload.resume_signature && eventPayload.resume_expires
                ? { signature: eventPayload.resume_signature, expires: eventPayload.resume_expires }
                : null
        );
    }

    initializeStyles();
    setBusinessLogo();
    setClientPortalSignInLink();
    renderAppointmentInfo();
    handleCalendarAndDirections();
    handleConfirmCancelButtons();
}

/** Dashboard base URL (telehealth + client portal live here). From API config widgets.dashboard.url. */
function getDashboardBaseUrl() {
    return form?.data?.dashboard_base_url || form?.project?.client_portal_base_url || form?.project?.dashboard_base_url || window.location.origin;
}

/** Sign-in URL for client portal: prefer backend-provided client_portal_url, else build from dashboard base + project uuid. */
function getClientPortalUrl() {
    const fromBackend = form?.data?.client_portal_url;
    if (fromBackend) return fromBackend;
    const baseClean = String(getDashboardBaseUrl()).replace(/\/$/, '');
    const projectUuid = form?.project?.uuid;
    return projectUuid ? `${baseClean}/client/portal/${projectUuid}` : `${baseClean}/client/portal`;
}

/** Default dashboard host when API does not send dashboard_base_url (telehealth must never use assistant or embed origin). */
const DEFAULT_DASHBOARD_BASE = 'https://dashboard.pilotpractice.com';

/** Join URL for video appointments: use dashboard host + /telehealth/{text_id} so users authenticate. Never use window.location.origin (could be assistant or embed). Fallback to room_url if no text_id. */
function getTelehealthJoinUrl() {
    if (!eventRoom) return null;
    const textId = eventRoom.text_id;
    if (textId) {
        const base = form?.data?.dashboard_base_url || form?.project?.dashboard_base_url || DEFAULT_DASHBOARD_BASE;
        const baseClean = String(base).replace(/\/$/, '');
        return `${baseClean}/telehealth/${textId}`;
    }
    return eventRoom.room_url || null;
}

function setClientPortalSignInLink() {
    const link = document.getElementById('header-sign-in-client-portal');
    if (!link) return;
    link.setAttribute('href', getClientPortalUrl());
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
}

function initializeStyles() {
    const container = document.querySelector(SELECTORS.container);
    if (!container) return;

    const informationCard = document.querySelector(SELECTORS.informationCard);
    if (informationCard) informationCard.style.backgroundColor = 'transparent';

}

function setBusinessLogo() {
    if (form.project.business_photo) {
        document.querySelector('.appointment__logo')?.setAttribute('src', form.project.business_photo);
    }
}

function renderAppointmentInfo() {
    const startTime = parseInt(eventPayload.start_time);
    const staffName = getStaffName();

    const staffNameDom = document.querySelector(SELECTORS.staffInfo);
    const timeInfoDom = document.querySelector(SELECTORS.timeInfo);
    const dateInfoDom = document.querySelector(SELECTORS.dateInfo);

    if (staffNameDom) staffNameDom.textContent = staffName;
    if (timeInfoDom) timeInfoDom.textContent = moment(startTime * 1000).format('hh:mm A');

    /* Date is shown in the title only; hide the redundant date line to avoid showing the date twice. */
    if (dateInfoDom) {
        dateInfoDom.textContent = '';
        dateInfoDom.style.display = 'none';
    }

    updateAppointmentTitle();
    renderAppointmentStatus();
    renderServicesRow();
}

function getStatusLabel(status) {
    switch (status) {
        case APPOINTMENT_STATUS.CONFIRMED:
            return t('confirmed');
        case APPOINTMENT_STATUS.REQUEST:
            return t('pending');
        case APPOINTMENT_STATUS.COMPLETE:
            return t('complete');
        case APPOINTMENT_STATUS.CANCELLED:
            return t('cancelled');
        default:
            return status ? String(status) : '';
    }
}

function renderAppointmentStatus() {
    const statusEl = document.querySelector('.appointment-confirmed__status');
    if (!statusEl) return;
    const status = eventPayload?.status;
    const label = getStatusLabel(status);
    if (!label) {
        statusEl.textContent = '';
        statusEl.style.display = 'none';
        return;
    }
    statusEl.textContent = label;
    statusEl.style.display = 'inline-block';
    statusEl.className = 'appointment-confirmed__status appointment-confirmed__status--' + (status || '');
}

function renderServicesRow() {
    const procedures = eventPayload.procedures ?? appointmentSummary?.event?.procedures ?? [];
    if (!procedures.length) return;

    const informationContainer = document.querySelector('.appointment-confirmed__body .information');
    if (!informationContainer) return;

    const existingRow = informationContainer.querySelector('.appointment-services__row');
    if (existingRow) existingRow.remove();

    const names = procedures.map((p) => procedureDisplayName(p) || p.title).filter(Boolean);
    if (!names.length) return;
    names.push(...getBookedAddonNames(procedures));

    const row = document.createElement('div');
    row.className = 'appointment-services__row';
    row.innerHTML = `
        <div class="appointment-services__label">${t('services')}</div>
        <div class="appointment-services__list">${names.join(', ')}</div>
    `;
    informationContainer.appendChild(row);
}

/**
 * Booked add-on names for the SERVICES row. Prefer the event payload
 * (procedure_addons — loaded on resume/confirm responses); right after an
 * in-session booking the create response may omit the relation, so fall
 * back to the visitor's selection (selectedAddons holds {id, procedureId})
 * resolved against the procedures' own procedure_addons lists.
 */
function getBookedAddonNames(procedures) {
    const fromPayload = eventPayload.procedure_addons ?? [];
    if (fromPayload.length) {
        return fromPayload.map((a) => a.title || a.name).filter(Boolean);
    }
    return selectedAddons
        .map((sel) => {
            if (sel.title || sel.name) return sel.title || sel.name;
            const proc = procedures.find((p) => p.id === sel.procedureId)
                ?? allProcedures.find((p) => p.id === sel.procedureId);
            const addon = proc?.procedure_addons?.find((a) => a.id === sel.id);
            return addon ? (addon.title || addon.name) : null;
        })
        .filter(Boolean);
}

function renderAddressRow() {
    if (eventRoom !== null) return;

    const informationContainer = document.querySelector('.appointment-confirmed__body .information');
    if (!informationContainer) return;

    const existingRow = informationContainer.querySelector('.location-address__row');
    if (existingRow) existingRow.remove();

    // A remote service (video or phone) can still carry a physical
    // location_id, so the address must be replaced with a "Virtual" /
    // "Phone Call" indicator rather than shown. Honor the channel flag
    // too — otherwise a virtual-only channel whose procedures are still
    // NULL (treated as in-person) prints the office street address.
    const procedures = eventPayload.procedures ?? appointmentSummary?.event?.procedures ?? [];

    if (isVirtualVisit(form, procedures)) {
        const allPhone = isPhoneBooking(procedures);
        const remoteRow = document.createElement('div');
        remoteRow.className = 'location-address__row';
        remoteRow.innerHTML = `
            <div class="location-address__label">
                <i data-feather="${allPhone ? 'phone' : 'video'}" class="location-address__icon"></i>
                <span class="location-address__text">${t(remoteVisitLabelKey(procedures))}</span>
            </div>
        `;
        informationContainer.appendChild(remoteRow);
        if (typeof feather !== 'undefined') {
            feather.replace();
        }
        return;
    }

    const locationObj = locations.find((l) => l.id === eventPayload.location_id);
    const address = locationObj?.full_address ?? eventPayload.location?.full_address ?? '';
    if (!address) return;

    const row = document.createElement('div');
    row.className = 'location-address__row';
    row.innerHTML = `
        <div class="location-address__label">
            <i data-feather="map-pin" class="location-address__icon"></i>
            <span class="location-address__text">${address}</span>
        </div>
        <button type="button" class="button--no-style location-address__copy" id="copy-address-confirmed" title="${t('copy')}" aria-label="${t('copy')}">
            <i data-feather="copy"></i>
            <span>${t('copy')}</span>
        </button>
    `;
    informationContainer.appendChild(row);
    if (typeof feather !== 'undefined') {
        feather.replace();
    }
}

/**
 * Solv-style terminal page for a revisit URL whose appointment can't be
 * loaded: cancelled (or gone entirely). No event details are available (the
 * URL carried no resume signature), so the page states what happened and
 * offers a rebook.
 */
export function loadAppointmentGonePage(status) {
    initializeStyles();
    setBusinessLogo();
    setClientPortalSignInLink();

    const body = document.querySelector(SELECTORS.body);
    if (!body) return;

    Array.from(body.children).forEach((child) => { child.style.display = 'none'; });

    const existing = body.querySelector('.appointment-gone');
    if (existing) existing.remove();

    const box = document.createElement('div');
    box.className = 'appointment-gone';
    box.style.cssText = 'width:100%;display:flex;flex-direction:column;align-items:center;text-align:center;gap:12px;margin-top:32px;';

    const title = document.createElement('div');
    title.style.cssText = "font-family:'Nunito',sans-serif;font-weight:700;font-size:20px;color:#374151;";
    title.textContent = status === 'cancelled'
        ? t('appointment_cancelled_page_title')
        : t('appointment_gone_page_title');

    const sub = document.createElement('div');
    sub.style.cssText = "font-family:'Nunito',sans-serif;font-size:15px;color:#7987a1;max-width:320px;";
    sub.textContent = t('appointment_gone_page_subtitle');

    const rebook = document.createElement('button');
    rebook.type = 'button';
    rebook.className = 'button--no-style appointment-action-btn appointment-action-btn--paperwork';
    rebook.style.cssText = 'width:100%;max-width:320px;margin-top:8px;';
    rebook.textContent = t('book_new_appointment');
    rebook.addEventListener('click', () => { openBookAppointment(); });

    box.appendChild(title);
    box.appendChild(sub);
    box.appendChild(rebook);
    body.appendChild(box);
}

function isAppointmentCancelled() {
    const hasSignature = hasValidSignature();

    if (!hasSignature) return false;

    if (eventPayload.status === APPOINTMENT_STATUS.CANCELLED) {
        return true;
    }

    if (eventPayload.status === APPOINTMENT_STATUS.CONFIRMED && eventPayload.participants && eventPayload.participants.length > 0) {
        const leadParticipant = eventPayload.participants.find(p => p.participable_type === PARTICIPANT_TYPES.LEAD);
        if (leadParticipant && leadParticipant.manually_confirmed === false) {
            return true;
        }
    }

    return false;
}

function updateAppointmentTitle() {
    const hasSignature = hasValidSignature();
    const titleContainer = document.querySelector(SELECTORS.title);
    const titleTextEl = titleContainer?.querySelector('.title-text');

    if (!titleTextEl) return;
    if (hasSignature && isAppointmentCancelled()) {
        titleTextEl.textContent = MESSAGES.ui.appointmentCancelled;
        if (titleContainer) titleContainer.style.color = COLORS.error;
    } else {
        const startTime = parseInt(eventPayload?.start_time, 10);
        const appointmentDate = startTime
            ? moment(startTime * 1000).format('dddd, MMM D, YYYY')
            : '';
        titleTextEl.textContent = appointmentDate || t('appointment_booked');
        if (titleContainer) titleContainer.style.color = '';
    }
}

function getStaffName() {
    if (eventPayload.participants && eventPayload.participants.length > 0 && params('event_id')) {
        const participant = eventPayload.participants.find(i => i.participable_type === 'App\\Models\\Staff');
        return participant ? `${participant.participable.first_name} ${participant.participable.last_name}` : '';
    }
    return getProviderFullName();
}

function handleCalendarAndDirections() {
    if (eventRoom === null) {
        handleEventRoomNullUI();
        renderAddressRow();
    } else {
        handleEventRoomUI();
        addJoinVideoButton();
    }

    addRescheduleCancelButtons();
}

function addJoinVideoButton() {
    if ((form?.appointment_channel?.show_video_call_button ?? true) === false) return;
    const joinUrl = getTelehealthJoinUrl();
    if (!joinUrl) return;

    const actionDiv = document.querySelector(SELECTORS.actionDiv);
    if (!actionDiv) return;

    const existing = actionDiv.querySelector('.appointment-join-video');
    if (existing) existing.remove();

    const link = document.createElement('a');
    link.id = 'join-video-call-link';
    link.className = 'appointment-join-video appointment-action-btn appointment-action-btn--video';
    link.href = joinUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = t('join_video_call');

    actionDiv.insertBefore(link, actionDiv.firstChild);
}

function addRescheduleCancelButtons() {
    const actionDiv = document.querySelector(SELECTORS.actionDiv);
    if (!actionDiv) return;

    // Cancelled appointment: no reschedule/cancel — offer a rebook instead.
    if (isAppointmentCancelled() || eventPayload.status === APPOINTMENT_STATUS.CANCELLED) {
        const stale = actionDiv.querySelector('.appointment-reschedule-cancel-buttons');
        if (stale) stale.remove();
        const rebookWrap = document.createElement('div');
        rebookWrap.className = 'appointment-reschedule-cancel-buttons';
        const rebookBtn = document.createElement('button');
        rebookBtn.type = 'button';
        rebookBtn.className = 'button--no-style appointment-action-btn appointment-action-btn--paperwork';
        rebookBtn.textContent = t('book_new_appointment');
        rebookBtn.addEventListener('click', () => { openBookAppointment(); });
        rebookWrap.appendChild(rebookBtn);
        actionDiv.appendChild(rebookWrap);
        return;
    }

    const existing = actionDiv.querySelector('.appointment-reschedule-cancel-buttons');
    if (existing) existing.remove();

    const wrapper = document.createElement('div');
    wrapper.className = 'appointment-reschedule-cancel-buttons';

    // The only post-booking handoff: visitor taps Start Paperwork on this
    // confirmation screen. Prefer embed_url (public template + slot token,
    // no OTP) over the assigned-form portal link.
    const paperworkUrl = eventPayload.paperwork?.embed_url || eventPayload.paperwork?.url;
    if (paperworkUrl) {
        const paperworkBtn = document.createElement('button');
        paperworkBtn.type = 'button';
        paperworkBtn.className = 'button--no-style appointment-action-btn appointment-action-btn--paperwork';
        paperworkBtn.textContent = t('start_paperwork');
        paperworkBtn.addEventListener('click', () => {
            try {
                window.top.location = paperworkUrl;
            } catch (_) {
                window.location = paperworkUrl;
            }
        });
        wrapper.appendChild(paperworkBtn);
    }

    const rescheduleBtn = document.createElement('button');
    rescheduleBtn.type = 'button';
    rescheduleBtn.className = 'button--no-style appointment-action-btn appointment-action-btn--reschedule';
    rescheduleBtn.textContent = t('reschedule');
    rescheduleBtn.addEventListener('click', async () => {
        rescheduleBtn.disabled = true;
        try {
            await runRescheduleFlow(eventPayload);
        } finally {
            rescheduleBtn.disabled = false;
        }
    });

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'button--no-style appointment-action-btn appointment-action-btn--cancel';
    cancelBtn.textContent = t('cancel_appointment');
    cancelBtn.addEventListener('click', () => {
        loadAppointmentCancel();
        appointmentCancelListeners();
        document.querySelector('.appointment-cancel__container')?.style?.setProperty('transition', 'none');
        navigate(SCREEN.APPOINTMENT_CANCEL);
    });

    wrapper.appendChild(rescheduleBtn);
    wrapper.appendChild(cancelBtn);
    actionDiv.appendChild(wrapper);

    if (typeof feather !== 'undefined') {
        feather.replace();
    }
}

function handleConfirmCancelButtons() {
    const { shouldShow } = shouldShowConfirmButtons();
    const { shouldShowPendingBanner } = shouldShowPendingApprovalBanner();
    const { shouldShowCancelledBanner: showCancelledBanner } = shouldShowCancelledBanner();
    const actionDiv = document.querySelector(SELECTORS.actionDiv);
    const existingButtonsContainer = document.querySelector(SELECTORS.appointmentButtons);

    toggleActionButtonsVisibility(actionDiv);

    // Legacy confirm/cancel pair is never rendered anymore; hide any stale one.
    if (!shouldShow && existingButtonsContainer) {
        existingButtonsContainer.style.display = 'none';
    }

    if (shouldShowPendingBanner) {
        createPendingApprovalBanner();
        removeCancelledBanner();
    } else {
        removePendingApprovalBanner();
    }

    if (showCancelledBanner) {
        createCancelledBanner();
        removePendingApprovalBanner();
    } else {
        removeCancelledBanner();
    }
}

function shouldShowConfirmButtons() {
    // Visitors never confirm their own appointments — confirmation is a
    // backend/staff concern. The manage page only offers Reschedule/Cancel.
    return { shouldShow: false };
}

function shouldShowPendingApprovalBanner() {
    if (eventPayload.status === APPOINTMENT_STATUS.REQUEST) {
        return { shouldShowPendingBanner: true };
    }

    return { shouldShowPendingBanner: false };
}

function shouldShowCancelledBanner() {
    const hasSignature = hasValidSignature();

    if (!hasSignature) {
        return { shouldShowCancelledBanner: false };
    }

    if (eventPayload.status === APPOINTMENT_STATUS.CANCELLED) {
        return { shouldShowCancelledBanner: true };
    }

    if (eventPayload.status === APPOINTMENT_STATUS.CONFIRMED && eventPayload.participants && eventPayload.participants.length > 0) {
        const leadParticipant = eventPayload.participants.find(p => p.participable_type === PARTICIPANT_TYPES.LEAD);
        if (leadParticipant && leadParticipant.manually_confirmed === false) {
            return { shouldShowCancelledBanner: true };
        }
    }

    return { shouldShowCancelledBanner: false };
}

function hasValidSignature() {
    const signature = params('signature');
    return signature && signature.length > 0;
}

function createPendingApprovalBanner() {
    const bodyContainer = document.querySelector(SELECTORS.body);
    const existingBanner = document.querySelector(SELECTORS.pendingBanner);

    if (existingBanner || !bodyContainer) return;

    const banner = document.createElement('div');
    banner.className = 'pending-approval-banner';
    banner.innerHTML = `
        <div class="banner-icon">
            <i data-feather="clock"></i>
        </div>
        <div class="banner-content">
            <div class="banner-title">${MESSAGES.banner.pendingApproval}</div>
            <div class="banner-description">${MESSAGES.banner.pendingApprovalDescription}</div>
        </div>
    `;

    const cardElement = document.querySelector(SELECTORS.card);
    if (cardElement) {
        cardElement.after(banner);
        if (typeof feather !== 'undefined') {
            feather.replace();
        }
    }
}

function removePendingApprovalBanner() {
    const existingBanner = document.querySelector(SELECTORS.pendingBanner);
    if (existingBanner) {
        existingBanner.remove();
    }
}

function createCancelledBanner() {
    const bodyContainer = document.querySelector(SELECTORS.body);
    const existingBanner = document.querySelector(SELECTORS.cancelledBanner);

    if (existingBanner || !bodyContainer) return;

    const banner = document.createElement('div');
    banner.className = 'cancelled-appointment-banner';
    banner.innerHTML = `
        <div class="banner-icon">
            <i data-feather="x-circle"></i>
        </div>
        <div class="banner-content">
            <div class="banner-title">${MESSAGES.banner.cancelled}</div>
            <div class="banner-description">${MESSAGES.banner.cancelledDescription}</div>
        </div>
    `;

    const cardElement = document.querySelector(SELECTORS.card);
    if (cardElement) {
        cardElement.after(banner);
        if (typeof feather !== 'undefined') {
            feather.replace();
        }
    }
}

function removeCancelledBanner() {
    const existingBanner = document.querySelector(SELECTORS.cancelledBanner);
    if (existingBanner) {
        existingBanner.remove();
    }
}

function toggleActionButtonsVisibility(actionDiv) {
    if (!actionDiv) return;

    const hasSignature = hasValidSignature();

    if (!hasSignature) {
        // Never offer Reschedule/Cancel on an already-cancelled appointment,
        // signature or not (the post-cancel redirect lands here unsigned).
        actionDiv.style.display = eventPayload.status === APPOINTMENT_STATUS.CANCELLED ? 'none' : 'flex';
        return;
    }

    const appointmentStatus = eventPayload.status;
    const leadParticipant = getLeadParticipant();
    const manuallyConfirmed = leadParticipant ? leadParticipant.manually_confirmed : null;

    const shouldDisplayActions = shouldShowActionButtons(appointmentStatus, manuallyConfirmed);

    actionDiv.style.display = shouldDisplayActions ? 'flex' : 'none';
}


function getLeadParticipant() {
    if (!eventPayload.participants || !eventPayload.participants.length) return null;
    return eventPayload.participants.find(p => p.participable_type === PARTICIPANT_TYPES.LEAD);
}

function shouldShowActionButtons(status, manuallyConfirmed) {
    if (status === APPOINTMENT_STATUS.REQUEST) {
        return false;
    }

    if (status === APPOINTMENT_STATUS.CONFIRMED && manuallyConfirmed === false) {
        return false;
    }

    if (status === APPOINTMENT_STATUS.CONFIRMED) {
        return true;
    }

    if (status === APPOINTMENT_STATUS.CANCELLED) {
        return false;
    }

    if (status === APPOINTMENT_STATUS.COMPLETE) {
        return true;
    }

    return true;
}

async function refreshAppointmentData(eventId) {
    // Refresh via the signature-gated resume route when the page carries a resume
    // signature (the standard "Manage appointment" / drop-off case); fall back to
    // the token-scoped endpoint only for legacy in-session links.
    const isDropoffSummary = params(DROPOFF_RESUME_PARAM) === 'summary';
    const resumeSignature = params('resume_signature') || (isDropoffSummary ? params('signature') : null);
    const resumeExpires = params('resume_expires') || (isDropoffSummary ? params('expires') : null);
    const updatedEventInfo = (resumeSignature && resumeExpires)
        ? await getEventResume(eventId, resumeSignature, resumeExpires)
        : await getEvevntInfoById(eventId);
    if (updatedEventInfo?.data) {
        setEventPayload(updatedEventInfo.data);
        updateUIAfterDataRefresh();
        return updatedEventInfo.data;
    }
    return null;
}

function updateUIAfterDataRefresh() {
    const { shouldShowPendingBanner } = shouldShowPendingApprovalBanner();
    const { shouldShowCancelledBanner: showCancelledBanner } = shouldShowCancelledBanner();

    if (shouldShowPendingBanner) {
        createPendingApprovalBanner();
        removeCancelledBanner();
    } else {
        removePendingApprovalBanner();
    }

    if (showCancelledBanner) {
        createCancelledBanner();
        removePendingApprovalBanner();
    } else {
        removeCancelledBanner();
    }

    updateAppointmentTitle();

    const actionDiv = document.querySelector(SELECTORS.actionDiv);
    toggleActionButtonsVisibility(actionDiv);

    const { shouldShow } = shouldShowConfirmButtons();
    const existingButtonsContainer = document.querySelector(SELECTORS.appointmentButtons);

    if (!shouldShow && existingButtonsContainer) {
        existingButtonsContainer.style.display = 'none';
    }
}

